import React, { useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Modal } from '@amzn/stencil-react-components/modal'
import { IconPlayFillSmall as IconPlayFill } from '@amzn/stencil-react-icons'

import { useVirtualCandidateSession } from 'src/components/virtual-candidate/useVirtualCandidateSession'
import { VirtualCandidateSessionView } from 'src/components/virtual-candidate/VirtualCandidateSessionView'
import {
    choiceFor,
    PersonaChoice,
    VirtualCandidateSetupView,
} from 'src/components/virtual-candidate/VirtualCandidateSetupView'
import { IscreenCompetencyDTO, IScreenDTO } from 'src/models/dto/activities/IScreenDTO'
import { CANDIDATE_PERSONAS } from 'src/services/virtual-candidate/personas'
import {
    deliverySessionRequest,
    EMPTY_JOB,
    VirtualCandidateJob,
} from 'src/services/virtual-candidate/sessionRequest'

export interface VirtualCandidatePlayButtonProps {
    iscreen: Pick<IScreenDTO, 'iscreenCompetencies'>
    pageIndex: number
    /** The module and the version open in the editor, whose last saved draft Delivery orders. */
    moduleId: string
    moduleVersionId: string
}

/**
 * Plays an iScreen page with a virtual candidate: opens a modal for the requisition and the
 * candidate's persona, then has Delivery order the module's last saved draft and streams the
 * interview live. Closing the modal ends the interview. The job and persona are kept for the next
 * run on this page.
 */
export const VirtualCandidatePlayButton = ({
    iscreen,
    pageIndex,
    moduleId,
    moduleVersionId,
}: VirtualCandidatePlayButtonProps) => {
    const { state, start, endInterview, close, reconnectAudio, disconnectAudio } =
        useVirtualCandidateSession()
    const [isOpen, setIsOpen] = useState(false)
    const [choice, setChoice] = useState<PersonaChoice>(() => choiceFor(CANDIDATE_PERSONAS[0]))
    const [job, setJob] = useState<VirtualCandidateJob>(EMPTY_JOB)
    const [started, setStarted] = useState<{
        job: VirtualCandidateJob
        competencies: IscreenCompetencyDTO[]
        personaName: string
    }>()
    const label = `Play page ${pageIndex + 1} with a virtual candidate`

    const onStart = () => {
        setStarted({
            job,
            competencies: iscreen.iscreenCompetencies,
            personaName: choice.name,
        })
        start(() =>
            deliverySessionRequest({ moduleId, moduleVersionId, job, prompt: choice.prompt })
        )
    }
    const onClose = () => {
        close()
        setStarted(undefined)
        setIsOpen(false)
    }

    return (
        <>
            <Button
                dataTestId={`virtual-candidate-play-${pageIndex + 1}`}
                variant={ButtonVariant.Secondary}
                aria-label={label}
                title={label}
                icon={<IconPlayFill aria-hidden />}
                onClick={() => setIsOpen(true)}
            />
            <Modal isOpen={isOpen} close={onClose} shouldCloseOnClickOutside={false}>
                {started ? (
                    <VirtualCandidateSessionView
                        state={state}
                        job={started.job}
                        competencies={started.competencies}
                        personaName={started.personaName}
                        onEndInterview={endInterview}
                        onReconnectAudio={reconnectAudio}
                        onDisconnectAudio={disconnectAudio}
                        onClose={onClose}
                    />
                ) : (
                    <VirtualCandidateSetupView
                        choice={choice}
                        onChoiceChange={setChoice}
                        job={job}
                        onJobChange={setJob}
                        onStart={onStart}
                        onCancel={onClose}
                    />
                )}
            </Modal>
        </>
    )
}
